import { useState } from 'react';
import type { AdPolicy, BloomRuntimeConfig, BloomUser, OfferPolicy } from '@apm/contracts';
import type { Tv2mInitResult, Tv2mLinkContext } from '@apm/tv2m-client-sdk';
import { useAdsEnabled } from '../ads/ads-enabled.js';
import { useOffersEnabled } from '../offers/offers-enabled.js';
import { useHomePreviewEnabled } from '../home/home-preview-enabled.js';
import { GoogleButton } from '../auth/GoogleButton.js';
import { googleRedirectOptions } from '../auth/callback.js';
import { loginMode, needsRedirectLogin, readDisplayFacts } from '../auth/login-mode.js';
import { NativeSignInButton } from '../auth/NativeSignInButton.js';
import { getNativeBridge } from '../native/bridge.js';
import type { AuthApi } from '../auth/use-auth.js';
import { NotificationPermission } from './NotificationPermission.js';
import type { PushEnabler } from './NotificationPermission.js';
import { useLocale, useT } from '../i18n/LocaleProvider.js';

/**
 * 설정 시트. **흡수된 모바일 웹의 홈이 이것이다** (`docs/14` B7).
 *
 * 통합 전에는 폰으로 모바일 웹을 직접 열었을 때 보이던 화면이었다. 한 오리진이
 * 된 뒤 `/` 는 시청 화면이므로, 그 내용을 방송 위에 겹치는 시트로 옮겼다 — **없애면
 * 안 된다.** 하는 일이 둘이고 둘 다 Tier 2 의 전제다.
 *
 * 1. 이 기기가 가구에 등록됐는지 보여 준다
 * 2. 알림을 켜게 한다
 *
 * TV 앞의 시청자가 `[모바일에서 구매]` 를 눌렀을 때 이 폰이 대상이 되려면 **등록과
 * 구독이 그 전에 끝나 있어야 한다** (`docs/07` §4 시나리오 B).
 *
 * 진단 링크가 여기 있는 이유는 iOS 다. 홈 화면에 추가한 앱은 주소를 바꿀 수 없어
 * `?debug=1` 로 갈 방법이 없다 (`docs/11`) — 앱 안의 링크가 유일한 입구다.
 */
export function SettingsSheet({
  init,
  subscribed,
  linkedToAccount,
  runtime,
  auth,
  ready,
  needsIosInstall,
  lastContext,
  adPolicy = null,
  offerPolicy = null,
  onOpenLast,
  onOpenChannels,
  onEnablePush,
  onClose,
}: {
  init: Tv2mInitResult | null;
  /** 서버에 구독이 붙었는가. 모드에 따라 진실의 출처가 다르다 (`useTv2m`) */
  subscribed: boolean;
  /** 이 기기가 계정에 묶였는가. **Tier 1 의 전제이고 화면에 드러나야 한다** */
  linkedToAccount: boolean;
  /** 구글 클라이언트 ID 를 여기서 읽는다. 없으면 로그인 입구를 그리지 않는다 */
  runtime: BloomRuntimeConfig;
  /**
   * 로그인·로그아웃 둘 다 이것으로 한다 (`docs/21` W10).
   *
   * **`LoginPage` 와 같은 경로다.** 복사하면 한쪽만 고쳐지고, 그 증상은 「어느 입구로
   * 들어왔느냐에 따라 로그인 뒤가 다르다」라 재현하기 어렵다.
   */
  auth: AuthApi;
  /** SDK 초기화가 끝났는가. 시청 화면은 이것을 기다리지 않으므로 시트가 직접 다룬다 */
  ready: boolean;
  /** iOS 인데 홈 화면에 추가되지 않아 Push 가 원천 불가능한가 (WB-08) */
  needsIosInstall: boolean;
  /** 이 기기가 마지막으로 열었던 상품. 없으면 `null` */
  lastContext: Tv2mLinkContext | null;
  /**
   * v21. 이 채널의 광고 정책. **`null` 이면 광고 줄 자체가 없다** (절대 규칙 56).
   *
   * 값을 읽지 않고 **유무만** 본다 — 무엇을 트는지는 서버가 정하고, 이 토글이 말하는
   * 것은 「지금 이 무대에서 광고를 보일 것인가」 하나다.
   */
  adPolicy?: AdPolicy | null;
  /**
   * v23. 이 채널의 상품 정책. **`null` 이면 상품 추천 줄 자체가 없다** (절대 규칙 66).
   *
   * **광고 줄과 따로 켜고 끈다** — 광고만 끈 채 상품을 보이는 것이 데모의 한 장면이고,
   * 하나로 묶으면 그 장면이 사라진다.
   */
  offerPolicy?: OfferPolicy | null;
  onOpenLast: () => void;
  /**
   * 채널 목록으로. **`null` 이면 그리지 않는다** — 공개 채널이 하나뿐이면 고를 것이
   * 없고, 없는 선택지를 보여 주면 눌러도 같은 화면으로 돌아온다.
   */
  onOpenChannels: (() => void) | null;
  onEnablePush: PushEnabler;
  onClose: () => void;
}) {
  const { locale, setLocale, t } = useLocale();
  // v21. **화면만 이 값을 구독한다** — 재생 경로는 어댑터 안에서 호출 시점에 읽는다.
  const [adsOn, setAdsOn] = useAdsEnabled();
  const [offersOn, setOffersOn] = useOffersEnabled();
  const [homePreviewOn, setHomePreviewOn] = useHomePreviewEnabled();

  return (
    /*
      `data-focus-scope` — 리모컨 포커스가 이 시트 밖으로 새지 않게 한다 (절대 규칙 32).
      **표시를 렌더하는 자리에 둔다** — 어댑터나 하네스에 셀렉터 목록을 두면 화면이 바뀔
      때 그 목록만 조용히 낡는다 (`data-i18n` 과 같은 방침, 절대 규칙 24).
      TV 계열이 아니면 아무도 읽지 않는 불활성 속성이다.
    */
    <div className="sheet" data-focus-scope>
      <div className="sheet__panel">
        <div className="sheet__head">
          <h2 className="sheet__title">{t('settings.title')}</h2>
          {/* `data-focus-back` — 뒤로 키가 누를 버튼. 이 층에 하나뿐이어야 한다 */}
          <button
            type="button"
            className="sheet__close"
            data-testid="sheet-close"
            data-focus-back
            onClick={onClose}
          >
            {t('common.close')}
          </button>
        </div>

        <AccountRow runtime={runtime} auth={auth} linkedToAccount={linkedToAccount} />

        <div className="badge-row">
          {!ready || init === null ? (
            <span className="badge">{t('settings.preparing')}</span>
          ) : init.householdId === null ? (
            // **등록 실패를 숨기지 않는다.** 이 상태에서는 Tier 2 가 이 기기를 찾지 못하고,
            // 증상은 "알림이 안 온다" 뿐이라 화면이 말해 주지 않으면 알 길이 없다.
            <span className="badge badge--error">{t('settings.householdFailed')}</span>
          ) : (
            <span className="badge badge--ok">{t('settings.householdOk')}</span>
          )}
        </div>

        <NotificationPermission
          init={init}
          subscribed={subscribed}
          needsIosInstall={needsIosInstall}
          onEnablePush={onEnablePush}
        />

        {/*
          같은 기기가 이미 본 상품이 있으면 이어서 열 수 있게 한다 (`last-context.ts`).
          A2HS 로 설치하고 아이콘으로 처음 들어온 순간 URL 에 아무것도 없는데, 그때
          시청자가 보러 온 상품이 여기 있다.
        */}
        {lastContext !== null && (
          <button type="button" className="button button--secondary" onClick={onOpenLast}>
            {t('settings.resumeLastProduct')}
          </button>
        )}

        {/*
          채널 바꾸기 (v9). **컨트롤 바가 아니라 시트다** — 거기는 이미 버튼이 셋이고
          (구매·정보·건너뛰기) 채널은 한 번 고르면 자주 바꾸지 않는 값이라 언어 전환과
          같은 자리가 맞는다.
        */}
        {onOpenChannels !== null && (
          <button
            type="button"
            className="button button--secondary"
            data-testid="open-channels"
            onClick={onOpenChannels}
          >
            {t('channels.change')}
          </button>
        )}

        {/*
          **언어 전환은 여기다** (`docs/22` §5.1). 컨트롤 바에는 이미 버튼이 셋이고
          (구매·정보·건너뛰기) 언어는 한 번 정하면 바꾸지 않는 값이라 시트가 맞는 자리다.

          **재생을 멈추지 않는다** (절대 규칙 25) — `setLocale` 은 상태만 바꾸고
          `location.reload()` 도 `key` 변경도 하지 않는다. 시트 뒤에서 영상이 계속 간다.
        */}
        {/*
          ★ v21 — 광고 On/Off (`docs/45` §9.8 · D25). **데모 장치이지 제품 기능이 아니다.**

          **`<input type="checkbox">` 도 스위치 엘리먼트도 아니다.** 포커스 후보가
          `button:not([disabled]), a[href]` 뿐이라(`use-tv-focus.ts` 의 `FOCUSABLE`)
          **리모컨으로 그 줄에 닿지 않는다** — 그런데 **마우스로는 멀쩡히 눌려**
          개발 기기에서는 영원히 드러나지 않고, TV 앞에서는 「리모컨이 죽었다」와
          구분되지 않는다 (절대 규칙 29). 언어 전환이 이미 **버튼 둘**인 이유가 그것이고,
          바로 아래 같은 모양으로 둔다.

          **`adPolicy === null` 이면 줄 자체가 없다** (절대 규칙 56) — 홈쇼핑 채널에서
          없는 것을 끌 수 있게 보이면 그것이 곧 「홈쇼핑에도 광고가 있다」는 신호다.
        */}
        {adPolicy !== null && (
          <div className="settings__locale" role="group" aria-label={t('settings.ads')}>
            <span className="settings__locale-label">{t('settings.ads')}</span>
            <div className="settings__locale-choices" data-testid="ads-switch">
              {([true, false] as const).map((value) => (
                <button
                  key={String(value)}
                  type="button"
                  className={`button button--secondary${adsOn === value ? ' is-selected' : ''}`}
                  aria-pressed={adsOn === value}
                  onClick={() => {
                    /*
                     * **리시버를 다시 세우지 않는다** — 어댑터가 `requestAds` 를 부르는
                     * 그 순간 이 값을 읽는다(`create-ad-adapter.ts`). 그래서 토글 한
                     * 번에 영상이 처음부터 다시 시작하지 않는다 (절대 규칙 25·51).
                     */
                    setAdsOn(value);
                  }}
                >
                  {value ? t('settings.adsOn') : t('settings.adsOff')}
                </button>
              ))}
            </div>
            <p className="settings__hint">{t('settings.adsHint')}</p>
          </div>
        )}

        {/*
          ★ v23 — 상품 추천 On/Off (`docs/47` §9.6 · 절대 규칙 60).
          **데모 장치이지 제품 기능이 아니다.**

          **버튼 둘이다.** `<input type="checkbox">` 도 스위치 엘리먼트도 아니다 —
          포커스 후보가 `button:not([disabled]), a[href]` 뿐이라 **리모컨으로 그 줄에
          닿지 않는데 마우스로는 멀쩡히 눌리고**, 개발 기기에서는 영원히 드러나지
          않는다 (절대 규칙 29). 위 광고 줄과 같은 모양이다.

          **`offerPolicy === null` 이면 줄 자체가 없다** (절대 규칙 66) — 홈쇼핑 채널에서
          없는 것을 끌 수 있게 보이면 그것이 곧 「홈쇼핑에도 상품이 있다」는 신호다.
        */}
        {offerPolicy !== null && (
          <div className="settings__locale" role="group" aria-label={t('settings.offers')}>
            <span className="settings__locale-label">{t('settings.offers')}</span>
            <div className="settings__locale-choices" data-testid="offers-switch">
              {([true, false] as const).map((value) => (
                <button
                  key={String(value)}
                  type="button"
                  className={`button button--secondary${offersOn === value ? ' is-selected' : ''}`}
                  aria-pressed={offersOn === value}
                  onClick={() => {
                    /*
                     * **리시버를 다시 세우지 않는다** — 어댑터가 `requestOffer` 를 부르는
                     * 그 순간 이 값을 읽는다(`create-offer-adapter.ts`). 그래서 토글 한
                     * 번에 영상이 처음부터 다시 시작하지 않는다 (절대 규칙 25·60).
                     *
                     * ★ **그 증상이 조용하다** — 상품은 정상적으로 멈추므로 토글 자체는
                     * 잘 도는 것처럼 보이고, 되감김은 **무대에서만** 밟는다.
                     */
                    setOffersOn(value);
                  }}
                >
                  {value ? t('settings.offersOn') : t('settings.offersOff')}
                </button>
              ))}
            </div>
            <p className="settings__hint">{t('settings.offersHint')}</p>
          </div>
        )}

        {/*
          v32 — 홈의 masthead 프리뷰 (`docs/63` §5.5 · D14). 위 광고·상품 줄과 **같은 모양**
          (`<button>` 둘)이다 — 체크박스면 리모컨으로 안 닿는다(절대 규칙 29). 저장은 기기다
          (`bloom.homePreview` · 기본 켬). 채널의 정책이 아니라 언제나 그린다.
        */}
        <div className="settings__locale" role="group" aria-label={t('settings.homePreview')}>
          <span className="settings__locale-label">{t('settings.homePreview')}</span>
          <div className="settings__locale-choices" data-testid="home-preview-switch">
            {([true, false] as const).map((value) => (
              <button
                key={String(value)}
                type="button"
                className={`button button--secondary${homePreviewOn === value ? ' is-selected' : ''}`}
                aria-pressed={homePreviewOn === value}
                onClick={() => {
                  setHomePreviewOn(value);
                }}
              >
                {value ? t('settings.homePreviewOn') : t('settings.homePreviewOff')}
              </button>
            ))}
          </div>
        </div>

        <div className="settings__locale" role="group" aria-label={t('settings.language')}>
          <span className="settings__locale-label">{t('settings.language')}</span>
          <div className="settings__locale-choices" data-testid="locale-switch">
            {(['ko', 'en'] as const).map((value) => (
              <button
                key={value}
                type="button"
                className={`button button--secondary${locale === value ? ' is-selected' : ''}`}
                aria-pressed={locale === value}
                onClick={() => {
                  setLocale(value);
                }}
              >
                {value === 'ko' ? '한국어' : 'English'}
              </button>
            ))}
          </div>
        </div>

        <a className="app__link" href="/debug">
          {t('settings.openDiagnostics')}
        </a>
      </div>
    </div>
  );
}

/**
 * 계정 줄.
 *
 * **「계정에 묶임」을 따로 보여 준다.** 로그인했는데 기기가 계정에 묶이지 않은 상태가
 * 실재하고(등록이 실패했거나 토큰이 만료된 채 등록됐거나), 그때 **Tier 1 이 이 폰을 찾지
 * 못한다.** 증상은 「TV 에서 눌렀는데 이 폰만 안 울린다」 하나라, 화면이 말하지 않으면
 * 로그인은 됐으니 괜찮다고 읽게 된다.
 */
function AccountRow({
  runtime,
  auth,
  linkedToAccount,
}: {
  runtime: BloomRuntimeConfig;
  auth: AuthApi;
  linkedToAccount: boolean;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const user: BloomUser | null = auth.user;

  if (user === null) {
    // 익명 정책(`BLOOM_REQUIRE_LOGIN=false`)이거나 로그인 없이 들어온 기기다.
    // Tier 2·4 는 그대로 성립하므로 오류가 아니다.
    return (
      <div className="card">
        <div className="badge-row">
          <span className="badge">{t('settings.notLoggedIn')}</span>
        </div>
        <AnonymousLogin runtime={runtime} auth={auth} />
      </div>
    );
  }

  return (
    <div className="card">
      <p className="card__text">{user.email}</p>
      <div className="badge-row">
        {linkedToAccount ? (
          <span className="badge badge--ok">{t('settings.deviceLinked')}</span>
        ) : (
          <span className="badge badge--error">{t('settings.deviceNotLinked')}</span>
        )}
      </div>
      <div style={{ marginTop: '0.9rem' }}>
        <button
          type="button"
          className="button button--secondary"
          disabled={busy}
          data-testid="logout"
          onClick={() => {
            setBusy(true);
            // 성공하면 페이지가 다시 열리므로 `finally` 가 도는 것은 실패했을 때뿐이다.
            void auth.logout().finally(() => {
              setBusy(false);
            });
          }}
        >
          {busy ? t('settings.signingOut') : t('settings.signOut')}
        </button>
      </div>
    </div>
  );
}

/**
 * 익명 상태의 로그인 입구 (`docs/21` W10).
 *
 * **이것이 없으면 `BLOOM_REQUIRE_LOGIN=false` 배포에서 화면으로 로그인할 방법이 없다.**
 * `LoginPage` 는 `App` 의 게이트로만 열리는데 그 게이트는 `requireLogin` 이 참일 때만
 * 서기 때문이다 — `docs/21` §9 의 데모 전환(A↔B 를 재기동 없이)이 화면에서 불가능했고,
 * §8 의 여섯째 조건이 **서버 관점에서만** 참이었다.
 *
 * **로그인 경로는 `LoginPage` 와 같은 것을 쓴다.** `auth.loginWithGoogle` 하나이고
 * 여기서 다시 구현하지 않는다 — 복사하면 한쪽만 고쳐지고, 그 증상은 「어느 입구로
 * 들어왔느냐에 따라 로그인 뒤가 다르다」라 재현하기 어렵다.
 *
 * ── 로그인 뒤 기기가 계정에 묶이는 자리 ───────────────────────
 *
 * `loginWithGoogle` 이 성공하면 **페이지를 다시 연다**(`window.location.replace`).
 * 그 재기동에서 `useTv2m` 이 새 토큰으로 `registerBloomDevice` 를 다시 부르고, 그때
 * `BloomDevice.userId` 가 채워진다 — **Tier 1 의 전제가 여기서 성립한다.** 상태만 바꾸고
 * 머물면 그 등록이 일어나지 않아 「로그인은 됐는데 이 폰만 안 울린다」가 된다.
 *
 * 그래서 **여기서 `registerBloomDevice` 를 따로 부르지 않는다.** 부르면 재기동 뒤에 한 번
 * 더 등록되고, 무엇보다 그 호출은 재기동에 밀려 결과를 쓸 곳이 없다.
 */
function AnonymousLogin({ runtime, auth }: { runtime: BloomRuntimeConfig; auth: AuthApi }) {
  const t = useT();
  // **판정은 `LoginPage` 와 같은 함수다** (v17 R4) — 복사하면 한쪽만 고쳐진다.
  const mode = loginMode(
    runtime,
    auth.user,
    getNativeBridge(),
    needsRedirectLogin(readDisplayFacts()),
  );

  /*
   * 로그인이 **선택**인 배포에서 클라이언트 ID 가 없는 것은 정상 구성이다
   * (`docs/12` §2.4 — 계정을 쓰지 않는 사업자 정책). `LoginPage` 는 같은 상황을
   * 오류로 안내하는데, 그쪽은 **로그인이 필요한데** 없는 경우라 뜻이 다르다.
   * 여기서는 입구를 그리지 않는 것으로 끝낸다.
   */
  if (mode.kind === 'none' || mode.kind === 'notConfigured') return null;

  return (
    <div style={{ marginTop: '0.9rem' }}>
      <p className="card__text">{t('settings.signInBenefit')}</p>
      <div style={{ marginTop: '0.6rem' }}>
        {mode.kind === 'native' ? (
          <NativeSignInButton
            signIn={mode.signIn}
            onIdToken={(idToken) => auth.loginWithGoogle(idToken)}
          />
        ) : mode.kind === 'shellTooOld' ? (
          /*
           * **여기서는 말한다.** 위 `notConfigured` 와 달리 「이 배포는 계정을 쓰지
           * 않는다」가 아니라 **「이 앱으로는 못 한다」**이고, 할 일이 재설치 하나로
           * 정해진다 — 침묵하면 시청자는 로그인 자체가 없는 줄 안다.
           */
          <p className="card__text">{t('login.shellTooOld')}</p>
        ) : (
          // 홈 화면 앱에서만 redirect 다 (`LoginPage` 의 같은 자리 주석).
          <GoogleButton
            clientId={mode.clientId}
            onCredential={(idToken) => {
              void auth.loginWithGoogle(idToken);
            }}
            {...(mode.kind === 'gisRedirect'
              ? { redirect: googleRedirectOptions(window.location.origin) }
              : {})}
          />
        )}
      </div>
      {auth.error !== null && <p className="card__text">{auth.error}</p>}
    </div>
  );
}
